import { combine, createStore } from 'effector'

import { BpmnEditorEvents } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.events'
import { NodeDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ActionStores } from '@flowx/designer/shared/effector/action/action.stores'
import { BoundaryEventStores } from '@flowx/designer/shared/effector/boundary-event/boundary-event.stores'
import { BpmnBoundaryEventEvents } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-boundary-event.events'
import { BpmnSequenceEvents } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-edge.events'
import { NodeStores } from '@flowx/designer/shared/effector/node/node.stores'
import { SequenceStores } from '@flowx/designer/shared/effector/sequence/sequence.stores'
import { NodeEntity } from '@flowx/designer/shared/entities/node.entity'

import { BpmnNodeEvents } from './bpmn-node.events'

import { SequencePoints } from '../../entities/sequence.dictionary'
import { AppEvents } from '../app/app.events'

export class BpmnEditorStores {
  static readonly multiselectionIds$ = createStore<NodeDefinition['id'][]>([], {
    name: 'multiselectionIds$',
  })
    .on(BpmnEditorEvents.selectionUpdated, (_, { entity }) => entity.map(({ id }) => id))
    .reset([
      BpmnEditorEvents.selectionCleared,
      BpmnEditorEvents.multiselectionCleared,
      AppEvents.processDiagramDestroy,
      BpmnEditorEvents.processVersionChanged,
      BpmnNodeEvents.nodeRightClick,
      BpmnNodeEvents.nodeLeftClick,
    ])

  static readonly multiselection$ = combine({
    multiselectionIds: BpmnEditorStores.multiselectionIds$,
    nodes: NodeStores.nodeDefinitions$,
  }).map(({ multiselectionIds, nodes }) => multiselectionIds.map((id) => nodes.get(id)))

  static readonly multipleEntitiesSelected$ = BpmnEditorStores.multiselection$.map(
    (selection) => selection.length > 0
  )

  static readonly readonly$ = createStore<boolean>(false, {
    name: 'readonly$',
  })
    .on(BpmnEditorEvents.onCanvasReadonly, (_, readonly) => readonly)
    .reset([AppEvents.processInstancePageDestroy, AppEvents.processDetailsPageDestroy])

  static readonly hoveredSequenceId$ = createStore(null, { name: 'hoveredSequenceId$' })
    .on(BpmnSequenceEvents.sequenceDragEnter, (_, sequenceId) => sequenceId)
    .on(BpmnSequenceEvents.sequenceDragLeave, () => null)

  static readonly selectedNodeId$ = createStore<NodeDefinition['id']>(null, {
    name: 'selectedNodeId$',
  })
    .on(BpmnNodeEvents.nodeSelectedByUrl, (_, id) => id)
    .on(
      [BpmnNodeEvents.nodeLeftClick, BpmnNodeEvents.nodeRightClick],
      (_, payload) => payload?.entity?.id ?? null
    )
    .on(
      [
        BpmnEditorEvents.searchSelectNode,
        BpmnEditorEvents.resetNodeOnUnsavedInfo,
        BpmnEditorEvents.processEditorSelectedNodeInNGRX,
        BpmnEditorEvents.nodeUiSelectedNodeInNGRX,
      ],
      (_, node) => node?.id ?? null
    )
    .on(BpmnEditorStores.multipleEntitiesSelected$, (state, multipleEntitiesSelected) =>
      multipleEntitiesSelected ? null : state
    )
    .on(
      [
        BpmnEditorEvents.clearSearch,
        BpmnEditorEvents.nodeDeselected,
        BpmnBoundaryEventEvents.boundaryLeftClick,
        BpmnEditorEvents.processVersionChanged,
      ],
      () => null
    )

  static readonly selectedNode$ = combine(
    { id: BpmnEditorStores.selectedNodeId$, nodes: NodeStores.nodeDefinitions$ },
    ({ id, nodes }) => {
      if (!id || !nodes) {
        return null
      }
      return nodes.get(id)
    }
  )

  static readonly selectedNodeDenormalized$ = combine(
    {
      id: BpmnEditorStores.selectedNodeId$,
      nodes: NodeStores.nodeDefinitions$,
      sequences: SequenceStores.sequences$,
      boundaryEvents: BoundaryEventStores.$boundaryEvents,
      actionDefinitions: ActionStores.$actionDefinitions,
    },
    ({ id, nodes, sequences, boundaryEvents, actionDefinitions }) => {
      if (!id || !nodes.get(id)) {
        return null
      }

      return NodeEntity.denormalizeNode(nodes.get(id), {
        sequences,
        boundaryEvents,
        actionDefinitions,
      })
    }
  )

  static readonly selectedSequenceId$ = createStore<SequencePoints['id']>(null, {
    name: 'selectedSequenceId$',
  })
    .on([BpmnSequenceEvents.sequenceLeftClick], (_, { sequenceId }) => sequenceId)
    .reset([
      AppEvents.processDiagramDestroy,
      BpmnEditorEvents.processVersionChanged,
      BpmnEditorEvents.selectionCleared,
      BpmnEditorEvents.canvasLeftClick,
      BpmnEditorEvents.swimlaneLeftClick,
      BpmnEditorEvents.swimlaneRightClick,
    ])
}
